import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import { sendEmail } from "../utils/sendEmail";
import { 
  FaStar, 
  FaBug, 
  FaLightbulb, 
  FaQuestionCircle,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaHeart,
  FaComments,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt
} from "react-icons/fa";

const Feedback = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    feedbackType: "",
    rating: 0,
    pageUrl: "",
    subject: "",
    message: "",
    screenshot: null,
    anonymous: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const feedbackTypes = [
    { value: "general", label: "General Feedback", icon: FaComments, color: "blue" },
    { value: "bug", label: "Bug Report", icon: FaBug, color: "red" },
    { value: "suggestion", label: "Feature Suggestion", icon: FaLightbulb, color: "green" },
    { value: "complaint", label: "Complaint", icon: FaExclamationTriangle, color: "orange" },
    { value: "compliment", label: "Compliment", icon: FaHeart, color: "pink" },
    { value: "question", label: "Question", icon: FaQuestionCircle, color: "purple" }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) { // 5MB limit
      setErrors(prev => ({
        ...prev,
        screenshot: "File size must be less than 5MB"
      }));
      return;
    }
    
    // Compress image if it's too large
    if (file && file.size > 1 * 1024 * 1024) { // Compress if > 1MB
      compressImage(file);
    } else {
      setFormData(prev => ({
        ...prev,
        screenshot: file
      }));
    }
  };

  const compressImage = (file) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (max 1200px width)
      const maxWidth = 1200;
      const maxHeight = 800;
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((compressedBlob) => {
        const compressedFile = new File([compressedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        setFormData(prev => ({
          ...prev,
          screenshot: compressedFile
        }));
      }, 'image/jpeg', 0.8); // 80% quality
    };
    
    img.src = URL.createObjectURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.feedbackType) {
      newErrors.feedbackType = "Please select a feedback type";
    }
    
    if (!formData.anonymous && !formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.anonymous && !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!formData.anonymous && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const to = process.env.REACT_APP_ELASTIC_TO;
    const subject = `Feedback from ${formData.anonymous ? "Anonymous" : formData.name}`;
    
    // Handle screenshot attachment
    let screenshotHtml = '';
    if (formData.screenshot) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.screenshot);
        });
        screenshotHtml = `
          <div style="margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; background-color: #f9f9f9;">
            <h3 style="margin: 0 0 10px 0; color: #333;">📎 Screenshot Attachment</h3>
            <p style="margin: 0 0 10px 0; color: #666;">File: ${formData.screenshot.name} (${(formData.screenshot.size / 1024).toFixed(1)} KB)</p>
            <img src="${base64}" alt="Screenshot" style="max-width: 100%; height: auto; border: 1px solid #ccc; border-radius: 4px;" />
          </div>
        `;
      } catch (error) {
        console.error("Error processing screenshot:", error);
        screenshotHtml = `<p style="color: red;">Error processing screenshot: ${formData.screenshot.name}</p>`;
      }
    }
    
    const body = `
      <h2>New Feedback Submission</h2>
      <p><strong>Type:</strong> ${formData.feedbackType}</p>
      <p><strong>Rating:</strong> ${formData.rating} Stars</p>
      <p><strong>Subject:</strong> ${formData.subject}</p>
      <p><strong>Message:</strong><br/>${formData.message}</p>
      ${!formData.anonymous ? `
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Phone:</strong> ${formData.phone || "N/A"}</p>
      ` : `<p><strong>Submitted Anonymously</strong></p>`}
      <p><strong>Related Page:</strong> ${formData.pageUrl || "N/A"}</p>
      ${screenshotHtml}
    `;

    try {
      console.log("Attempting to send feedback email");
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const cleanURL = API_BASE_URL.replace(/\/$/, ''); // Remove trailing slash
      console.log("API_BASE_URL:", cleanURL);
      
      const response = await fetch(`${cleanURL}/api/send-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: subject,
          body: body,
          replyTo: !formData.anonymous ? formData.email : null,
          replyToName: !formData.anonymous ? formData.name : null
        })
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Feedback email result:", result);
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        console.error("Email failed:", result);
        alert(`Failed to send feedback: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Email sending error:", error);
      alert(`Error sending feedback: ${error.message}`);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="mb-8">
                <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  Thank You for Your Feedback!
                </h1>
                <p className="text-lg text-gray-600 mb-6">
                  Your feedback has been successfully submitted. We appreciate you taking the time to help us improve.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold text-blue-800 mb-3">What happens next?</h3>
                <ul className="text-left text-blue-700 space-y-2">
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    We'll review your feedback within 24-48 hours
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    If you provided contact information, we'll follow up if needed
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    Your suggestions will be considered for future improvements
                  </li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/" 
                  className="bg-custom-pink text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-600 transition-colors"
                >
                  Return to Home
                </Link>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Submit Another Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23933eea' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
            Share Your <span className="text-custom-pink">Feedback</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Help us improve your experience at JRIIT. Your feedback is valuable to us and helps us serve you better.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <FaUser className="mr-2" />
              <span>Anonymous feedback welcome</span>
            </div>
            <div className="flex items-center">
              <FaEnvelope className="mr-2" />
              <span>Quick response guaranteed</span>
            </div>
            <div className="flex items-center">
              <FaHeart className="mr-2" />
              <span>Your voice matters</span>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-custom-pink to-pink-600 text-white p-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Tell Us What You Think</h2>
                <p className="text-pink-100">
                  Your feedback helps us improve our services and create a better experience for everyone.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                {/* Feedback Type Selection */}
                <div className="mb-8">
                  <label className="block text-lg font-semibold text-gray-700 mb-4">
                    What type of feedback would you like to share? *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {feedbackTypes.map((type) => {
                      const IconComponent = type.icon;
                      
                      // Define explicit classes for each feedback type
                      const getSelectedClasses = (color) => {
                        switch (color) {
                          case 'blue':
                            return 'border-blue-500 bg-blue-50';
                          case 'red':
                            return 'border-red-500 bg-red-50';
                          case 'green':
                            return 'border-green-500 bg-green-50';
                          case 'orange':
                            return 'border-orange-500 bg-orange-50';
                          case 'pink':
                            return 'border-pink-500 bg-pink-50';
                          case 'purple':
                            return 'border-purple-500 bg-purple-50';
                          default:
                            return 'border-gray-500 bg-gray-50';
                        }
                      };
                      
                      const getIconClasses = (color) => {
                        switch (color) {
                          case 'blue':
                            return 'text-blue-500';
                          case 'red':
                            return 'text-red-500';
                          case 'green':
                            return 'text-green-500';
                          case 'orange':
                            return 'text-orange-500';
                          case 'pink':
                            return 'text-pink-500';
                          case 'purple':
                            return 'text-purple-500';
                          default:
                            return 'text-gray-500';
                        }
                      };
                      
                      return (
                        <label
                          key={type.value}
                          className={`relative cursor-pointer p-4 rounded-lg border-2 transition-all duration-200 ${
                            formData.feedbackType === type.value
                              ? getSelectedClasses(type.color)
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="feedbackType"
                            value={type.value}
                            checked={formData.feedbackType === type.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-center space-x-3">
                            <IconComponent className={`text-2xl ${getIconClasses(type.color)}`} />
                            <span className="font-medium text-gray-700">{type.label}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {errors.feedbackType && (
                    <p className="text-red-500 text-sm mt-2">{errors.feedbackType}</p>
                  )}
                </div>

                {/* Overall Rating */}
                <div className="mb-8">
                  <label className="block text-lg font-semibold text-gray-700 mb-4">
                    How would you rate your overall experience? *
                  </label>
                  <div className="flex justify-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingClick(star)}
                        className={`text-4xl transition-colors duration-200 ${
                          star <= formData.rating
                            ? 'text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-300'
                        }`}
                      >
                        <FaStar />
                      </button>
                    ))}
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-sm text-gray-500">
                      {formData.rating === 0 && "Click to rate"}
                      {formData.rating === 1 && "Poor"}
                      {formData.rating === 2 && "Fair"}
                      {formData.rating === 3 && "Good"}
                      {formData.rating === 4 && "Very Good"}
                      {formData.rating === 5 && "Excellent"}
                    </span>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="anonymous"
                      name="anonymous"
                      checked={formData.anonymous}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <label htmlFor="anonymous" className="text-gray-700 font-medium">
                      Submit feedback anonymously
                    </label>
                  </div>
                  
                  {!formData.anonymous && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent ${
                            errors.name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your full name"
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent ${
                            errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your email address"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                        )}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Subject and Message */}
                <div className="mb-8">
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent ${
                        errors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Brief description of your feedback"
                    />
                    {errors.subject && (
                      <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                    )}
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent resize-none ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Please provide detailed feedback about your experience..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                    )}
                  </div>
                </div>

                {/* Page URL */}
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Which page or section does this feedback relate to? (Optional)
                  </label>
                  <input
                    type="url"
                    name="pageUrl"
                    value={formData.pageUrl}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
                    placeholder="https://jriit.edu.pk/..."
                  />
                </div>

                {/* Screenshot Upload */}
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Screenshot (Optional)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-custom-pink transition-colors">
                    <FaUpload className="text-3xl text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    <input
                      type="file"
                      name="screenshot"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="hidden"
                      id="screenshot-upload"
                    />
                    <label
                      htmlFor="screenshot-upload"
                      className="inline-block mt-2 bg-custom-pink text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-pink-600 transition-colors"
                    >
                      Choose File
                    </label>
                    {formData.screenshot && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ {formData.screenshot.name} selected ({(formData.screenshot.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                    {errors.screenshot && (
                      <p className="text-red-500 text-sm mt-2">{errors.screenshot}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-custom-pink text-white px-12 py-4 rounded-lg font-semibold text-lg hover:bg-pink-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
                  >
                    Submit Feedback
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">
              Other Ways to Reach Us
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-blue-50 rounded-lg p-6">
                <FaEnvelope className="text-3xl text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Email Us</h3>
                <p className="text-gray-600 mb-2"> principal@jriit.ac.tz</p>
                <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <FaPhone className="text-3xl text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Call Us</h3>
                <p className="text-gray-600 mb-2">+255 754 360 590</p>
                <p className="text-sm text-gray-500">Mon-Fri, 9AM-5PM</p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6">
                <FaMapMarkerAlt className="text-3xl text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Visit Us</h3>
                <p className="text-gray-600 mb-2">JRIIT Campus</p>
                <p className="text-sm text-gray-500">Come see us in person</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default Feedback;